import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './GuidedTour.css';

const GuidedTour = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [targetRect, setTargetRect] = useState(null);
    const [tourKey, setTourKey] = useState(null);
    const observerRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!tourKey) return;
        const element = document.querySelector(`[data-tour="${tourKey}"]`);
        if (element) {
            const rect = element.getBoundingClientRect();
            setTargetRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            });
        }
    }, [tourKey]);

    const clearTour = useCallback(() => {
        setTargetRect(null);
        setTourKey(null);
        if (observerRef.current) observerRef.current.disconnect();
        
        const params = new URLSearchParams(location.search);
        if (params.has('tour')) {
            params.delete('tour');
            const newSearch = params.toString();
            navigate({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : ''
            }, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tour = params.get('tour');

        if (tour) {
            setTourKey(tour);
            // Wait for transitions and layout stabilization
            const timer = setTimeout(() => {
                const element = document.querySelector(`[data-tour="${tour}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Recalculate slightly after scroll starts
                    setTimeout(updatePosition, 300);
                }
            }, 600);

            return () => clearTimeout(timer);
        } else {
            setTargetRect(null);
            setTourKey(null);
        }
    }, [location.search, updatePosition]);

    useEffect(() => {
        if (tourKey) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, { passive: true });
            
            const handleGlobalClick = () => clearTour();
            const timer = setTimeout(() => {
                document.addEventListener('mousedown', handleGlobalClick);
            }, 500);

            // Observe element resizing (if content changes)
            const targetElement = document.querySelector(`[data-tour="${tourKey}"]`);
            if (targetElement && window.ResizeObserver) {
                observerRef.current = new ResizeObserver(updatePosition);
                observerRef.current.observe(targetElement);
            }

            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition);
                document.removeEventListener('mousedown', handleGlobalClick);
                clearTimeout(timer);
                if (observerRef.current) observerRef.current.disconnect();
            };
        }
    }, [tourKey, updatePosition, clearTour]);

    if (!targetRect || !tourKey) return null;

    return (
        <div className="guided-tour-overlay">
            <div
                className="guided-tour-highlight"
                style={{
                    top: targetRect.top - 6,
                    left: targetRect.left - 6,
                    width: targetRect.width + 12,
                    height: targetRect.height + 12
                }}
            >
                <div className="guided-tour-pulse" />
                <div className="guided-tour-label">Buraya Tıkla</div>
            </div>
        </div>
    );
};

export default GuidedTour;
